import type { ConceptoPago, EstadoDisputa, EstadoPago, FormaCalculo, FormaPago, MotivoReembolso, ReglaVariosServicios, Transferencia } from '../../api/ops/pagos';
import type { EstadoChip } from '../../componentes/Chip';
import type { NombreIcono } from '../../componentes/iconos';
import { ErrorApi } from '../../api/problema';
import type { TarjetaAyuda } from '../../marco/cabecera';

/**
 * Textos de Pagos (4.1 a 4.8), tal cual de la exportación (`diseno/pagos/`) salvo donde una decisión la corrige:
 * - G120, G276: los apartados, la entrada «Cobrar» y la tarjeta de «Negocio» solo llegan a quien tiene su permiso.
 * - G121, G140: sin cifras sacadas de listas; las únicas cifras son las `badge` del servidor y las del resumen.
 * - G159: «Deshacer» solo en lo que no mueve dinero ni avisa a nadie (versión de una política, archivarla, asignarla y
 *   «Dejarla como está»).
 * - G214: las palabras del negocio (`vocabulary`), sin «mesa» fija.
 * - G277: los textos de los códigos de Stripe son los `label` de la API.
 * - G282: Pagos no cobra el no-show ni la cancelación tardía: lo pide el módulo dueño; aquí solo «Reintentar».
 * - G288, G289: sin lista de «Pendiente de cobrar hoy»; «Registrados por ti» es solo lo de esta sesión.
 * - G290: Conciliación con el `net` y el periodo anterior de la API, sin minigráfico ni porcentajes calculados.
 */
export const PERMISOS = {
    cuentaVer: 'pagos.cuenta.ver',
    cuentaConectar: 'pagos.cuenta.conectar',
    politicas: 'pagos.politicas.gestionar',
    pagosVer: 'pagos.pagos.ver',
    reembolsar: 'pagos.pagos.reembolsar',
    decidir: 'pagos.reembolsos.decidir',
    cobrar: 'pagos.cobros.crear',
    disputas: 'pagos.disputas.responder',
    registrar: 'pagos.externos.registrar',
    anular: 'pagos.externos.anular',
    exportar: 'pagos.pagos.exportar',
    resumen: 'pagos.resumen.ver',
    clientes: 'clientes.fichas.ver',
} as const;

const ayuda = (filas: readonly (readonly [NombreIcono, string, string])[]): TarjetaAyuda[] => filas.map(([icono, titulo, texto]) => ({ icono, titulo, texto }));

export const AYUDA = {
    cuenta: ayuda([
        ['landmark', 'Tu cuenta de Stripe', 'Cobras en tu propia cuenta de Stripe, con el país y la moneda de tu negocio. El dinero va directo a ella.'],
        ['shield-check', 'Tus datos, en Stripe', 'Identidad, banco y tarjetas los guarda Stripe. Reservaria solo ve el estado de la cuenta.'],
        ['triangle-alert', 'Si Stripe pide algo', 'Te avisamos en la campana. Mientras la cuenta esté restringida no se cobra online; lo ya cobrado y los reembolsos siguen.'],
        ['unplug', 'Desconectar', 'Solo el propietario. Antes ves qué deja de cobrarse; con disputas o reembolsos abiertos no se puede.'],
    ]),
    politicas: ayuda([
        ['shield-check', 'Políticas de cobro', 'Garantía, señal y cargos por no-show y cancelación tardía. Las asignas a zonas, recursos o servicios.'],
        ['history', 'Versiones', 'Cada cambio crea una versión nueva. Lo ya reservado sigue con la versión que aceptó el cliente.'],
        ['rotate-ccw', 'Cancelar a tiempo', 'Si el cliente cancela antes del plazo, se le devuelve la señal.'],
        ['archive', 'Archivar', 'Una política asignada no se archiva. Las archivadas se conservan para las reservas que la usaron.'],
    ]),
    asignar: ayuda([
        ['list-checks', 'Dónde se aplican', 'Elige la política de cada zona, recurso o servicio. Una como mucho en cada sitio.'],
        ['armchair', 'Zonas y recursos', 'Un recurso sin política usa la de su zona. Si alguno tiene una propia, en una reserva de zona manda la más estricta.'],
        ['undo-2', 'Al momento', 'El cambio vale para las reservas nuevas. Tienes 5 segundos para deshacerlo.'],
    ]),
    pagos: ayuda([
        ['credit-card', 'Pagos', 'Todo lo que cobras: online con Stripe y en el local (efectivo o datáfono). Cada pago dice de qué viene.'],
        ['rotate-ccw', 'Reembolsar', 'Total o en parte. Nunca más de lo cobrado: se descuentan las devoluciones anteriores, también las hechas en Stripe.'],
        ['scale', 'En disputa', 'Si el banco del cliente abre una disputa, ese pago no se puede reembolsar hasta que se resuelva.'],
    ]),
    caja: ayuda([
        ['banknote', 'Cobrar en el local', 'Anota cómo te han pagado: efectivo o el datáfono del negocio. No hay cierre de caja: lo lleva vuestro programa de contabilidad.'],
        ['ban', '¿Te has equivocado?', 'Pide a un administrador que lo anule. No se borra: queda la anulación con su motivo.'],
    ]),
    disputas: ayuda([
        ['scale', 'Disputas', 'El banco del cliente reclama un cargo. Tienes hasta la fecha límite para enviar pruebas; si no respondes, se pierde.'],
        ['paperclip', 'Pruebas', 'Reservaria prepara el recibo y las condiciones aceptadas. Añade fotos o documentos que expliquen lo que pasó.'],
        ['calendar-check', 'La reserva no cambia', 'Mientras dura, la reserva, la cita o el pedido solo se marcan «pago en disputa».'],
    ]),
    conciliacion: ayuda([
        ['landmark', 'Conciliación', 'Lo cobrado en el periodo, lo devuelto y las comisiones, para cuadrarlo con tu banco y tu contabilidad.'],
        ['landmark', 'Transferencias', 'Stripe ingresa lo cobrado en tu banco. Abre cada una para ver qué pagos la forman.'],
        ['download', 'Exportar', 'Un CSV con pagos, devoluciones, disputas y comisiones del periodo, con las referencias de Stripe.'],
    ]),
};

/** Estado de un pago: palabra y color (misma palabra, mismo color; componentes.md §7). */
export const ESTADO_PAGO: Readonly<Record<EstadoPago, readonly [string, EstadoChip]>> = {
    pending: ['Pendiente', 'pending'],
    processing: ['En proceso', 'pending'],
    guarantee_saved: ['Tarjeta guardada', 'confirmed'],
    released: ['Liberada', 'neutral'],
    completed: ['Pagado', 'confirmed'],
    partially_refunded: ['Devuelto en parte', 'confirmed'],
    refunded: ['Devuelto', 'confirmed'],
    disputed: ['En disputa', 'problem'],
    lost_in_dispute: ['Perdido en disputa', 'problem'],
    expired: ['Caducado', 'problem'],
    rejected: ['Fallido', 'problem'],
    registered: ['Pagado', 'confirmed'],
    voided: ['Anulado', 'problem'],
};

/** En la lista, el chip solo sale si hay que actuar (4.4). */
export const ESTADO_CON_CHIP: ReadonlySet<EstadoPago> = new Set(['pending', 'processing', 'disputed', 'lost_in_dispute', 'rejected']);

export const CONCEPTO: Readonly<Record<ConceptoPago, string>> = {
    sale: 'Venta',
    deposit: 'Señal',
    guarantee: 'Tarjeta de garantía',
    no_show_fee: 'Cargo por no venir',
    late_cancellation_fee: 'Cancelación tardía',
    balance: 'Resto en el local',
};

export const FORMA_PAGO: Readonly<Record<FormaPago, readonly [string, NombreIcono]>> = {
    card: ['Tarjeta', 'credit-card'],
    wallet: ['Apple Pay o Google Pay', 'smartphone'],
    bank_debit: ['Adeudo', 'landmark'],
    bank_transfer: ['Transferencia', 'landmark'],
    cash: ['Efectivo', 'banknote'],
    card_terminal: ['Datáfono', 'credit-card'],
};

/** De qué es cada pago: el tipo del `payable` (el detalle es su `description`, G283). */
export function tipoCobrable(tipo: string): readonly [string, NombreIcono] {
    if (tipo.includes('appointment')) {
        return ['Cita', 'calendar'];
    }
    if (tipo.includes('ticket') || tipo.includes('order')) {
        return ['Entradas', 'ticket'];
    }
    if (tipo.includes('reservation')) {
        return ['Reserva', 'calendar-check'];
    }
    return ['Cobro', 'receipt'];
}

export const MOTIVOS_REEMBOLSO: readonly (readonly [MotivoReembolso, string])[] = [
    ['customer_cancelled_in_time', 'El cliente canceló a tiempo'],
    ['customer_cancelled', 'El cliente canceló fuera de plazo'],
    ['business_cancelled', 'Cancelamos nosotros'],
    ['organizer_cancelled', 'El organizador canceló el evento'],
    ['date_changed', 'Cambio de fecha'],
    ['duplicate', 'Cobro duplicado'],
    ['goodwill', 'Gesto comercial'],
    ['other', 'Otro motivo'],
];

export const ORIGEN_REEMBOLSO = { request: 'desde Reservaria', stripe_dashboard: 'desde el panel de Stripe', external: 'devuelto en el local' } as const;

export const ESTADO_DISPUTA: Readonly<Record<EstadoDisputa, readonly [string, EstadoChip]>> = {
    open: ['Abierta', 'pending'],
    evidence_submitted: ['Pruebas enviadas', 'neutral'],
    won: ['Ganada', 'confirmed'],
    lost: ['Perdida', 'problem'],
};

export const ESTADO_TRANSFERENCIA: Readonly<Record<Transferencia['status'], readonly [string, EstadoChip]>> = {
    pending: ['Pendiente', 'pending'],
    in_transit: ['En camino', 'pending'],
    paid: ['Ingresada', 'confirmed'],
    failed: ['Fallida', 'problem'],
    canceled: ['Cancelada', 'problem'],
};

export const FORMA_CALCULO: Readonly<Record<FormaCalculo, readonly [string, string]>> = {
    per_person: ['Por persona', 'por persona'],
    per_booking: ['Por reserva', 'por reserva'],
    per_unit: ['Por unidad', 'por unidad'],
    per_night: ['Por noche', 'por noche'],
};

export const VARIOS_SERVICIOS: Readonly<Record<ReglaVariosServicios, string>> = {
    single_service: 'Una señal: la más alta (por defecto)',
    sum_all: 'La suma de las señales',
};

/** Ayuda de «Citas de varios servicios» en el editor (4.2, G433), marcada «Texto a validar». */
export const AYUDA_VARIOS_SERVICIOS = 'Si los servicios de la cita tienen plazos de cancelación gratuita distintos, vale el más largo.';

/** «24 h» o «3 días». */
export function horas(h: number): string {
    return h >= 48 && h % 24 === 0 ? `${String(h / 24)} días` : `${String(h)} h`;
}

/** Texto de un error de la API para un aviso breve (el `detail` del Problem o el de por defecto). */
export function textoDeError(e: unknown, porDefecto: string): string {
    if (e instanceof ErrorApi) {
        if (e.codigo === 'version_conflict') {
            return 'Alguien lo ha cambiado mientras lo mirabas. Ábrelo de nuevo.';
        }
        if (e.codigo === 'provider_error') {
            return 'Stripe no ha respondido. Inténtalo de nuevo en un momento.';
        }
        return e.problema.detail ?? porDefecto;
    }
    return porDefecto;
}

/** Lo que queda hasta una fecha límite: «20 h 35 min» o «4 días y 6 h» (y «20 h» o «4 d» en corto). */
export function quedaPara(iso: string, ahora: Date = new Date()): { largo: string; corto: string; urgente: boolean; vencida: boolean } {
    const min = Math.floor((new Date(iso).getTime() - ahora.getTime()) / 60_000);
    if (Number.isNaN(min) || min <= 0) {
        return { largo: '0 min', corto: '0 h', urgente: true, vencida: true };
    }
    const dias = Math.floor(min / 1440);
    const h = Math.floor((min % 1440) / 60);
    const m = min % 60;
    if (dias >= 2) {
        return { largo: h > 0 ? `${String(dias)} días y ${String(h)} h` : `${String(dias)} días`, corto: `${String(dias)} d`, urgente: false, vencida: false };
    }
    const horasTotales = Math.floor(min / 60);
    return { largo: horasTotales > 0 ? `${String(horasTotales)} h ${String(m)} min` : `${String(m)} min`, corto: horasTotales > 0 ? `${String(horasTotales)} h` : `${String(m)} min`, urgente: true, vencida: false };
}

/** «jueves, 22 de octubre, 16:56». */
export function fechaLarga(iso: string): string {
    const f = new Date(iso);
    return Number.isNaN(f.getTime()) ? '' : f.toLocaleString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });
}
